import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export interface ReportActionKey {
  key: string;
  description?: string;
}

const SKIP_ACTION_KEYS = new Set(["end_call"]);

export const MUST_HAVE_CALL_ACTION_KEYS: ReportActionKey[] = [
  { key: "need_review" },
  { key: "available_time" },
];

export const formatActionTitle = (key: string) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

export const toActionLabel = formatActionTitle;

export const normalizeActionValue = (val: any) => {
  if (typeof val === "string") {
    if (/[<>]/.test(val)) {
      return val.replace(/<[^>]*>/g, "").trim() || val;
    }
    return val;
  }
  return JSON.stringify(val);
};

export const formatTimeSlot = (slot: any) => {
  const { start_time, end_time } = slot || {};
  if (!start_time && !end_time) return "-";
  try {
    const startTime = start_time ? dayjs.utc(start_time) : null;
    const endTime = end_time ? dayjs.utc(end_time) : null;
    if (!startTime && endTime)
      return `${endTime.format("h:mm A")}, ${endTime.format("MMM DD, YYYY")}`;
    if (startTime && !endTime)
      return `${startTime.format("h:mm A")}, ${startTime.format(
        "MMM DD, YYYY"
      )}`;
    if (startTime?.isValid() && endTime?.isValid()) {
      if (startTime.isSame(endTime, "day")) {
        return `${startTime.format("h:mm A")} - ${endTime.format(
          "h:mm A"
        )}, ${startTime.format("MMM DD, YYYY")}`;
      }
      return `${startTime.format("h:mm A")}, ${startTime.format(
        "MMM DD, YYYY"
      )} - ${endTime.format("h:mm A")}, ${endTime.format("MMM DD, YYYY")}`;
    }
    return `${start_time || ""} - ${end_time || ""}`.replace(/^- | -$/, "") || "-";
  } catch {
    return `${start_time || ""} - ${end_time || ""}`.replace(/^- | -$/, "") || "-";
  }
};

const serializeActionItem = (item: any): string => {
  if (item && typeof item === "object") {
    const hasTime = Boolean(item.start_time || item.end_time);
    const parts: string[] = [];
    if (hasTime) {
      const label =
        item.start_time && item.end_time
          ? `${toActionLabel("start_time")} - ${toActionLabel("end_time")}`
          : item.start_time
          ? toActionLabel("start_time")
          : toActionLabel("end_time");
      parts.push(`${label}: ${formatTimeSlot(item)}`);
    }
    Object.keys(item)
      .filter((k) => k !== "start_time" && k !== "end_time")
      .forEach((k) => {
        parts.push(`${toActionLabel(k)}: ${normalizeActionValue(item[k])}`);
      });
    return parts.join(" | ");
  }
  return normalizeActionValue(item);
};

export const formatActionValueForCsv = (actionData: any): string => {
  if (
    !actionData?.value ||
    (Array.isArray(actionData.value) && actionData.value.length === 0)
  ) {
    return "-";
  }
  const items = Array.isArray(actionData.value)
    ? actionData.value
    : [actionData.value];
  if (!items.length) return "-";
  return items.map(serializeActionItem).join(";\n");
};

export const formatCallSummaryForCsv = (row: any): string => {
  const callAnalysis = row?.call_analysis || {};
  const followUp = callAnalysis.follow_up;
  const summary = callAnalysis.summary;
  const reason = callAnalysis.reason;
  const recommendation =
    typeof followUp === "string" ? followUp : followUp?.recommendation;
  if (summary || reason || recommendation) {
    const parts: string[] = [];
    if (summary) parts.push(`Summary: ${summary}`);
    if (reason) parts.push(`Reason: ${reason}`);
    if (recommendation) parts.push(`Follow up: ${recommendation}`);
    return parts.join(" | ");
  }
  const fallback = String(row?.summary || "").trim();
  return fallback || "-";
};

export const collectTakenActionKeys = (rows: any[]): ReportActionKey[] => {
  const map = new Map<string, ReportActionKey>();
  for (const record of rows || []) {
    const action = record?.action;
    if (!action || typeof action !== "object" || Array.isArray(action)) continue;
    Object.entries(action).forEach(([key, value]: [string, any]) => {
      if (SKIP_ACTION_KEYS.has(key)) return;
      const isActionTaken =
        value && typeof value === "object" && value.action_taken === true;
      if (!isActionTaken) return;
      if (!map.has(key)) {
        map.set(key, { key, description: value.description });
      }
    });
  }
  return Array.from(map.values());
};

export const mergeActionKeys = (
  ...lists: Array<ReportActionKey[] | undefined>
): ReportActionKey[] => {
  const map = new Map<string, ReportActionKey>();
  for (const list of lists) {
    for (const item of list || []) {
      if (!item?.key || SKIP_ACTION_KEYS.has(item.key)) continue;
      const existing = map.get(item.key);
      map.set(item.key, {
        key: item.key,
        description: existing?.description || item.description,
      });
    }
  }
  return Array.from(map.values());
};

export const resolveCallActionKeys = (
  rows: any[],
  extraActionKeys?: ReportActionKey[]
): ReportActionKey[] =>
  mergeActionKeys(
    MUST_HAVE_CALL_ACTION_KEYS,
    extraActionKeys,
    collectTakenActionKeys(rows)
  );
